import { Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { switchMap, tap } from 'rxjs';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { DatabaseModelApiService } from '../../database-model/services/database-model-api/database-model-api.service';
import { DiagramApiService } from '../../../core/services/diagram/diagram.api.service';
import { SavedDatabaseModel } from '../../database-model/models/saved/saved-database-model.type';
import { SavedSequenceDiagram } from '../../../core/model/diagram/sequenceDiagram';
import { SVG_CREATE_DASHBOARD, SVG_DELETE_DASHBOARD, SVG_VIEW_DASHBOARD } from '../constant/auth.constant';
import { SafeHtmlPipe } from '../../../core/pipe/safe-html.pipe';


@Component({
  selector: 'app-user-dashboard',
  standalone: true,
  imports: [SafeHtmlPipe],
  templateUrl: './user-dashboard.component.html',
  styleUrl: './user-dashboard.component.scss',
})
export class UserDashboardComponent {
  private _databaseModelApiService = inject(DatabaseModelApiService);
  private _diagramApiService = inject(DiagramApiService);
  private _toast = inject(ToastrService);
  private _router = inject(Router);
  private _destroyRef = inject(DestroyRef);

  public databaseModels = signal<SavedDatabaseModel[]>([]);
  public sequenceDiagrams = signal<SavedSequenceDiagram[]>([]);
  public activeTab = signal<string>('models');

  public readonly svgView = SVG_VIEW_DASHBOARD;
  public readonly svgDelete = SVG_DELETE_DASHBOARD;
  public readonly svgCreate = SVG_CREATE_DASHBOARD;

  constructor() {
    this._loadData();
  }

  private _loadData(): void {
    this._databaseModelApiService
      .getAllDatabaseModels$()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(models => {
        this.databaseModels.set(models);
      });

    this._diagramApiService
      .getAllDiagrams()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(diagrams => {
        this.sequenceDiagrams.set(diagrams);
      });
  }

  public getDatabaseModelById(id: number): void {
    this._databaseModelApiService.getDatabaseModelById$(id).subscribe({
      next: () => this._router.navigate(['/database-model', id]),
      error: () => this._toast.error('Erreur lors de la récupération du modèle.', 'Erreur'),
    });
  }

  public deleteDatabaseModel(id: number): void {
    this._databaseModelApiService
      .deleteDatabaseModel$(id)
      .pipe(
        tap(() => console.log('Suppression DatabaseModel effectuée, rechargement...')),
        switchMap(() => this._databaseModelApiService.getAllDatabaseModels$()),
        tap(models => {
          console.log('Nouveaux DatabaseModels reçus:', models);
          this.databaseModels.set(models);
        })
      )
      .subscribe({
        next: () => this._toast.success('Le modèle a été supprimé avec succès.', 'Succès'),
        error: err => {
          console.error('Erreur lors de la suppression du modèle:', err);
          this._toast.error('Erreur lors de la suppression du modèle.', 'Erreur');
        },
      });
  }

  public selectTab(tabName: string): void {
    this.activeTab.set(tabName);
  }

  public getDiagramById(id: number): void {
    this._diagramApiService.getDiagramById(id).subscribe({
      next: diagram => {
        if (diagram) {
          void this._router.navigate(['/sequence-diagram', id]);
        } else {
          this._toast.error("Le diagramme demandé n'existe pas.", 'Erreur');
        }
      },
      error: () => this._toast.error('Erreur lors de la récupération du diagramme.', 'Erreur'),
    });
  }

  public deleteDiagram(id: number): void {
    this._diagramApiService
      .deleteDiagram(id)
      .pipe(
        switchMap(() => this._diagramApiService.getAllDiagrams()),
        tap(diagrams => this.sequenceDiagrams.set(diagrams))
      )
      .subscribe({
        next: () => this._toast.success('Le diagramme a été supprimé avec succès.', 'Succès'),
        error: () => this._toast.error('Erreur lors de la suppression du diagramme.', 'Erreur'),
      });
  }

  public navigateToCreateModel(): void {
    this._router.navigate(['/database-model/menu']);
  }

  public navigateToCreateDiagram(): void {
    this._router.navigate(['/sequence-diagram']);
  }
}
